import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import BigNumber from 'bignumber.js'
import { useWeb3React } from '@web3-react/core'
import { Flex, Text } from 'uikit'
import orderBy from 'lodash/orderBy'
import partition from 'lodash/partition'
import { useTranslation } from 'contexts/Localization'
import usePersistState from 'hooks/usePersistState'
import { usePools, useFetchPublicPoolsData } from 'state/hooks'
import { latinise } from 'utils/latinise'
import FlexLayout from 'components/Layout/Flex'
import Page from 'components/Layout/Page'
import SearchInput from 'components/SearchInput'
import Select, { OptionProps } from 'components/Select/Select'
import { Pool } from 'state/types'
import PoolCard from './components/PoolCard'
import PoolTabButtons from './components/PoolTabButtons'
import { getAprData } from './helpers'

const Hero = styled.div`
  background-image: linear-gradient(94.61deg, ${({ theme }) => theme.colors.background2} 15.09%, #9012fe 90.14%);
  ${({ theme }) => theme.mediaQueries.md} {
    background-image: url('/images/pools/3d-model.png'),
      linear-gradient(94.61deg, ${({ theme }) => theme.colors.background2} 15.09%, #9012fe 90.14%); /* W3C */
  }
  background-repeat: no-repeat;
  background-position: top right 15%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: left;
  color: white;
  height: 245px;
  margin: auto;
  margin-bottom: 32px;
  padding-left: 40px;
  // -webkit-box-shadow: 0px 4px 4px 0px ${({ theme }) => theme.colors.background2};
  // box-shadow: 0px 4px 4px 0px ${({ theme }) => theme.colors.background2};

  ${({ theme }) => theme.mediaQueries.md} {
    border-radius: 24px;
  }
`

const Title = styled.div`
  font-size: 50px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #ffffff;
`

const Subtitle = styled.div`
  font-weight: 300;
  font-size: 18px;
`

const CardLayout = styled(FlexLayout)`
  justify-content: center;
`

const ControlStretch = styled(Flex)`
  > div {
    flex: 1;
  }
`

const PoolControls = styled.div`
  display: flex;
  flex-direction: row;
  align-items: end;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 16px;
  margin-bottom: 64px;

  ${({ theme }) => theme.mediaQueries.md} {
    padding: 16px 32px;
    margin-bottom: 32px;
  }
`

const TabWrapper = styled.div``

const SwitchWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  width: 100%;
  margin-bottom: 16px;

  ${({ theme }) => theme.mediaQueries.md} {
    width: auto;
    margin-bottom: 0px;
  }
`

const SearchWrapper = styled.div`
  display: flex;
  width: 100%;
  padding: 8px 0px;
  justify-content: flex-end;
  align-items: end;

  ${({ theme }) => theme.mediaQueries.md} {
    justify-content: flex-end;
    width: auto;
    padding: 0;
  }
`

const NUMBER_OF_POOLS_VISIBLE = 12

const Pools: React.FC = () => {
  const location = useLocation()
  const { t } = useTranslation()
  const { account } = useWeb3React()
  const { pools } = usePools(account)
  const [stakedOnly, setStakedOnly] = usePersistState(false, { localStorageKey: 'pancake_pool_staked' })
  const [numberOfPoolsVisible, setNumberOfPoolsVisible] = useState(NUMBER_OF_POOLS_VISIBLE)
  const [observerIsSet, setObserverIsSet] = useState(false)
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortOption, setSortOption] = useState('hot')

  // TODO aren't arrays in dep array checked just by reference, i.e. it will rerender every time reference changes?
  const [finishedPools, openPools] = useMemo(() => partition(pools, (pool) => pool.isFinished), [pools])
  const stakedOnlyFinishedPools = useMemo(
    () => finishedPools.filter((pool) => pool.userData && new BigNumber(pool.userData.stakedBalance).isGreaterThan(0)),
    [finishedPools],
  )
  const stakedOnlyOpenPools = useMemo(
    () => openPools.filter((pool) => pool.userData && new BigNumber(pool.userData.stakedBalance).isGreaterThan(0)),
    [openPools],
  )
  const hasStakeInFinishedPools = stakedOnlyFinishedPools.length > 0

  useFetchPublicPoolsData()

  useEffect(() => {
    const showMorePools = (entries) => {
      const [entry] = entries
      if (entry.isIntersecting) {
        setNumberOfPoolsVisible((poolsCurrentlyVisible) => poolsCurrentlyVisible + NUMBER_OF_POOLS_VISIBLE)
      }
    }

    if (!observerIsSet) {
      const loadMoreObserver = new IntersectionObserver(showMorePools, {
        rootMargin: '0px',
        threshold: 1,
      })
      loadMoreObserver.observe(loadMoreRef.current)
      setObserverIsSet(true)
    }
  }, [observerIsSet])

  const showFinishedPools = location.pathname.includes('history')

  const handleChangeSearchQuery = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  const handleSortOptionChange = (option: OptionProps): void => {
    setSortOption(option.value)
  }

  const sortPools = (poolsToSort: Pool[]) => {
    switch (sortOption) {
      case 'apr':
        // Ternary is needed to prevent pools without APR (like MIX) getting top spot
        return orderBy(poolsToSort, (pool: Pool) => (pool.apr ? getAprData(pool).apr : 0), 'desc')
      case 'earned':
        return orderBy(
          poolsToSort,
          (pool: Pool) => {
            if (!pool.userData || !pool.earningTokenPrice) {
              return 0
            }
            return pool.userData.pendingReward.times(pool.earningTokenPrice).toNumber()
          },
          'desc',
        )
      case 'totalStaked':
        return orderBy(poolsToSort, (pool: Pool) => pool.totalStaked.toNumber(), 'desc')
      default:
        return poolsToSort
    }
  }

  const poolsToShow = () => {
    let chosenPools = []
    if (showFinishedPools) {
      chosenPools = stakedOnly ? stakedOnlyFinishedPools : finishedPools
    } else {
      chosenPools = stakedOnly ? stakedOnlyOpenPools : openPools
    }

    if (searchQuery) {
      const lowercaseQuery = latinise(searchQuery.toLowerCase())
      chosenPools = chosenPools.filter(() => latinise('dmd').includes(lowercaseQuery))
    }

    return sortPools(chosenPools).slice(0, numberOfPoolsVisible)
  }

  const cardLayout = (
    <CardLayout>
      {poolsToShow().map((pool) => (
        <PoolCard key={pool.sousId} pool={pool} account={account} />
      ))}
    </CardLayout>
  )

  return (
    <>
      <Page>
        <Hero>
          <Title>{t('Pools')}</Title>
          <Subtitle>{t('Stake single tokens to earn DMD')}</Subtitle>
        </Hero>
        <PoolControls>
          <SwitchWrapper>
            <PoolTabButtons
              stakedOnly={stakedOnly}
              setStakedOnly={setStakedOnly}
              hasStakeInFinishedPools={hasStakeInFinishedPools}
            />
          </SwitchWrapper>
          <SearchWrapper>
            <TabWrapper>
              <Text fontSize="9px" textTransform="uppercase">
                {t('Sort by')}
              </Text>
              <Select
                options={[
                  {
                    label: t('Hot'),
                    value: 'hot',
                  },
                  {
                    label: t('APR'),
                    value: 'apr',
                  },
                  {
                    label: t('Earned'),
                    value: 'earned',
                  },
                  {
                    label: t('Total staked'),
                    value: 'totalStaked',
                  },
                ]}
                onChange={handleSortOptionChange}
              />
            </TabWrapper>
            <TabWrapper style={{ marginLeft: 16 }}>
              <Text fontSize="9px" textTransform="uppercase">
                {t('Search')}
              </Text>
              <ControlStretch>
                <SearchInput onChange={handleChangeSearchQuery} placeholder="Search Pools" />
              </ControlStretch>
            </TabWrapper>
          </SearchWrapper>
        </PoolControls>
        {showFinishedPools && (
          <Text fontSize="20px" color="failure" pb="32px">
            {t('These pools are no longer distributing rewards. Please unstake your tokens.')}
          </Text>
        )}
        {cardLayout}
        <div ref={loadMoreRef} />
      </Page>
    </>
  )
}

export default Pools
